import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Building2, Calculator, Check, Eye, FileCheck, HeartHandshake, Landmark, Layers,
  RefreshCw, Rocket, ShieldCheck, Sparkles, Target, TrendingUp, Users2, Zap,
} from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { Reveal } from "@/components/landing/Reveal";
import { AmbientOrbs } from "@/components/landing/Ambient";
import { ScrollProgress, WhatsAppFab } from "@/components/landing/Floating";
import { FlowDiagram } from "@/components/landing/FlowDiagram";
import { INDUSTRIES } from "@/lib/data";
import SEO from "@/components/SEO";
import { getOrganizationSchema } from "@/lib/seoSchemas";

const WHAT_WE_DO = [
  { title: "Business Formation", icon: Building2, items: ["Private Limited Company", "LLP Registration", "Partnership Firm", "Proprietorship", "One Person Company (OPC)", "Section 8 Company", "Business Structuring"] },
  { title: "Taxation & Regulatory Compliance", icon: Landmark, items: ["GST Registration & Compliance", "Income Tax Services", "TDS Compliance", "ROC & MCA Compliance", "Labour Law Compliance", "Tax Representation & Assessment Support"] },
  { title: "Accounting & Financial Management", icon: Calculator, items: ["Accounting & Bookkeeping", "Payroll Management", "Financial Reporting", "MIS & Business Reporting", "Virtual CFO Services", "Budgeting & Cash Flow Planning"] },
  { title: "Business Licences & Registrations", icon: FileCheck, items: ["Udyam Registration", "Shop & Establishment Registration", "Professional Tax", "IEC Registration", "FSSAI Registration", "Digital Signature Certificate (DSC)", "Other Regulatory Approvals"] },
  { title: "Business Advisory & Growth", icon: TrendingUp, items: ["Project Reports", "Financial Advisory", "Startup Advisory", "Compliance Planning", "Business Documentation", "Loan Documentation Support", "Business Process Advisory"] },
];

// The two growth-path examples from the source copy, rendered as a
// step-chain infographic instead of an arrow-separated sentence.
const GROWTH_PATH_NEW_CO = ["Registration", "GST", "Accounting", "Payroll", "ROC Compliance", "Taxation", "Financial Reporting"];
const GROWTH_PATH_MSME = ["Accounting", "MIS", "Cash Flow", "Funding", "Compliance", "Business Advisory"];

const WHO_WE_SERVE = ["Entrepreneurs & Startups", "MSMEs", "Private Limited Companies", "LLPs & Partnership Firms", "Proprietorship Businesses", "NGOs & Section 8 Companies", "Manufacturers", "Traders", "Service Providers", "Professionals", "Educational Institutions"];

const WHY_US = [
  {
    icon: Layers,
    title: "One Point of Coordination",
    desc: "Where multiple services are taken through Biz360, information can be coordinated across accounting, taxation, compliance, finance and business advisory, so a business owner does not have to repeatedly explain the same background to different service providers.",
  },
  {
    icon: Sparkles,
    title: "Technology, Without Losing the Human Side",
    desc: "We use digital tools wherever they make the process simpler — document sharing, online consultations, communication, workflow tracking and compliance reminders — but someone still needs to understand the numbers, the documents and the business situation behind them.",
  },
  {
    icon: ShieldCheck,
    title: "Professional Service Model",
    desc: "For assignments involving statutory audit, professional certification, legal representation or other functions required by law to be performed by a specifically authorised professional, the relevant work is undertaken or certified by the appropriately qualified professional, while our role may include preparation, documentation, financial analysis, coordination and implementation support.",
  },
];

const VALUES = [
  { icon: Eye, title: "Clarity", desc: "We explain what is required and why." },
  { icon: Target, title: "Accuracy", desc: "We work carefully with financial and statutory information." },
  { icon: Zap, title: "Responsiveness", desc: "We keep clients informed about requirements, progress and pending information." },
  { icon: RefreshCw, title: "Continuity", desc: "We aim to build long-term professional relationships rather than one-time transactions." },
];

const APPROACH = [
  { title: "Understand", desc: "Business requirements and objectives." },
  { title: "Plan", desc: "Solution design and documentation roadmap." },
  { title: "Execute", desc: "Registrations, filings and professional implementation." },
  { title: "Support", desc: "Ongoing compliance assistance and advisory." },
];

const TECH_DELIVERY = ["Secure document sharing", "Digital workflow management", "Online consultation", "Automated compliance reminders", "Centralised communication", "Timely status updates"];

// Renders a sequence of labels as connected nodes — an infographic
// "growth path" instead of an arrow-joined sentence.
const SectionHeading = ({ icon: SIcon, children }) => (
  <h2 className="flex items-center gap-2.5 text-2xl font-semibold tracking-tight text-navy-900 md:text-3xl">
    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-emerald2-500/10 text-emerald2-600">
      <SIcon className="h-4 w-4" />
    </span>
    {children}
  </h2>
);

export default function About() {
  return (
    <div className="relative min-h-screen bg-white font-sans text-navy-900 antialiased" data-testid="about-page">
      <SEO
        title="About Biz360 Compliance | Expert Legal, Tax & Financial Advisory"
        description="Learn about Biz360 Compliance — our team of qualified professionals, service methodology, and mission to deliver seamless compliance across Maharashtra & India."
        canonical="/about"
        keywords="about Biz360, business compliance team Pune, tax consultants Maharashtra, corporate legal advisors India"
        breadcrumbs={[{ label: "About Us", href: "/about" }]}
        schema={getOrganizationSchema()}
      />
      <ScrollProgress />
      <Navbar />
      <main>
        <section className="relative overflow-hidden bg-[rgb(238,242,250)] pt-32 pb-16 md:pt-40 md:pb-20">
          <AmbientOrbs />
          <div className="relative mx-auto max-w-4xl px-5 text-center lg:px-8">
            <Reveal>
              <span className="inline-flex items-center rounded-full border border-emerald2-300/40 bg-emerald2-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-emerald2-600">
                About Biz360 Compliance
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="mt-4 text-3xl font-bold tracking-tight text-navy-900 md:text-5xl">
                Helping Businesses Manage the Work Behind the Business
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-5 text-base leading-relaxed text-slate-600 md:text-lg">
                We work with entrepreneurs, startups, MSMEs, companies, LLPs, partnerships and other organisations that need
                dependable support across business registration, taxation, accounting, compliance, finance and business advisory.
              </p>
            </Reveal>
          </div>
        </section>

        <div className="relative mx-auto max-w-4xl px-5 py-16 lg:px-8 md:py-20">
          <div className="space-y-14">
            <Reveal>
              <div className="rounded-3xl bg-navy-900 p-8 md:p-10">
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-gold-400">More Than Filing &amp; Compliance</h3>
                <div className="mt-4 space-y-3 text-sm leading-relaxed text-white/70 md:text-base">
                  <p>A business may approach us initially for a GST registration, company incorporation or tax return.</p>
                  <p>But in practice, one requirement often leads to another.</p>
                </div>

                <div className="mt-6">
                  <p className="text-xs font-bold uppercase tracking-wider text-gold-400/80">A newly incorporated company may need:</p>
                  <div className="mt-3"><FlowDiagram steps={GROWTH_PATH_NEW_CO} dark /></div>
                </div>
                <div className="mt-6">
                  <p className="text-xs font-bold uppercase tracking-wider text-gold-400/80">An established MSME may need:</p>
                  <div className="mt-3"><FlowDiagram steps={GROWTH_PATH_MSME} dark /></div>
                </div>

                <p className="mt-6 text-sm leading-relaxed text-white/70 md:text-base">
                  We therefore look at the business requirement as a whole, rather than treating every assignment as an isolated filing.
                </p>
              </div>
            </Reveal>

            <Reveal>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-[rgb(238,242,250)]/60 p-6">
                  <h3 className="text-sm font-bold text-navy-900">Our Objective</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">Make the professional and compliance side of running a business easier to manage.</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-[rgb(238,242,250)]/60 p-6">
                  <h3 className="text-sm font-bold text-navy-900">Built for the Long Term</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    A business's requirements change over time. Today, an entrepreneur may need help incorporating a company.
                    Tomorrow, the same business may need GST and Income Tax support, monthly accounting, payroll, ROC compliance,
                    MIS reporting, working capital planning, funding documentation and business advisory. We aim to remain useful
                    as those requirements evolve.
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal>
              <SectionHeading icon={Layers}>What We Do</SectionHeading>
              <p className="mt-3 text-[15px] text-slate-600">Biz360 Compliance provides integrated business solutions under one platform.</p>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                {WHAT_WE_DO.map((group, i) => (
                  <motion.div
                    key={group.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: false, margin: "-60px" }}
                    transition={{ duration: 0.45, delay: i * 0.06 }}
                    whileHover={{ y: -3 }}
                    className="rounded-2xl border border-slate-200 bg-[rgb(238,242,250)]/60 p-5 transition-colors hover:border-emerald2-300"
                  >
                    <div className="flex items-center gap-2.5">
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-emerald2-500/10 text-emerald2-600">
                        <group.icon className="h-4 w-4" />
                      </span>
                      <h3 className="text-sm font-bold text-navy-900">{group.title}</h3>
                    </div>
                    <ul className="mt-3 space-y-2">
                      {group.items.map((item) => (
                        <li key={item} className="flex items-start gap-2 text-sm text-slate-600">
                          <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald2-600" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </Reveal>

            <Reveal>
              <SectionHeading icon={Users2}>Who We Serve</SectionHeading>
              <div className="mt-5 flex flex-wrap gap-2.5">
                {WHO_WE_SERVE.map((item, i) => (
                  <motion.span
                    key={item}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: false, margin: "-60px" }}
                    transition={{ duration: 0.3, delay: i * 0.03 }}
                    className="rounded-full border border-navy-900/10 bg-navy-50 px-4 py-2 text-sm font-medium text-navy-800"
                  >
                    {item}
                  </motion.span>
                ))}
              </div>
              <p className="mt-4 text-[15px] leading-relaxed text-slate-600">
                While Biz360 Compliance delivers services 100% digitally to businesses across India, our corporate headquarters in Pune provides deep statutory grounding for enterprises operating under Maharashtra jurisdictions — including ROC Pune, ROC Mumbai, Maharashtra Shop &amp; Establishment, and commercial hubs across Mumbai, Parbhani, Chhatrapati Sambhajinagar, Nagpur, and Marathwada.
              </p>
            </Reveal>

            <Reveal>
              <SectionHeading icon={HeartHandshake}>Why Businesses Work With Us</SectionHeading>
              <p className="mt-3 text-[15px] text-slate-600">
                Rather than coordinating with multiple consultants, our clients benefit from a single platform supported by
                experienced professionals, structured workflows and technology-enabled service delivery.
              </p>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                {WHY_US.map((w, i) => (
                  <motion.div
                    key={w.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: false, margin: "-60px" }}
                    transition={{ duration: 0.45, delay: i * 0.08 }}
                    whileHover={{ y: -3 }}
                    className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_4px_16px_rgba(0,0,0,0.03)] transition-colors hover:border-emerald2-300"
                  >
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-emerald2-500/10 text-emerald2-600">
                      <w.icon className="h-5 w-5" />
                    </span>
                    <div>
                      <h4 className="font-semibold text-navy-900">{w.title}</h4>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">{w.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </Reveal>

            <Reveal>
              <SectionHeading icon={Rocket}>Our Service Approach</SectionHeading>
              <p className="mt-3 text-[15px] text-slate-600">Every engagement follows a structured process designed to ensure clarity, accuracy and timely delivery.</p>
              <div className="relative mt-6">
                <div className="absolute left-0 right-0 top-[18px] hidden h-px bg-gradient-to-r from-navy-900/0 via-navy-900/15 to-navy-900/0 lg:block" aria-hidden="true" />
                <ol className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                  {APPROACH.map((step, i) => (
                    <motion.li
                      key={step.title}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: false, margin: "-60px" }}
                      transition={{ duration: 0.5, delay: i * 0.1 }}
                      whileHover={{ y: -3 }}
                      className="relative rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_4px_16px_rgba(0,0,0,0.03)]"
                    >
                      <span className="relative z-10 grid h-9 w-9 place-items-center rounded-full bg-navy-900 text-sm font-bold text-gold-400">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <h4 className="mt-3 font-semibold text-navy-900">{step.title}</h4>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">{step.desc}</p>
                    </motion.li>
                  ))}
                </ol>
              </div>
            </Reveal>

            <Reveal>
              <SectionHeading icon={ShieldCheck}>Technology-Driven Service Delivery</SectionHeading>
              <p className="mt-3 text-[15px] text-slate-600">To improve efficiency and client experience, Biz360 Compliance follows a digital-first approach.</p>
              <ul className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {TECH_DELIVERY.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-slate-600">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald2-600" />
                    {item}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal>
              <SectionHeading icon={Layers}>Industries We Support</SectionHeading>
              <div className="mt-5 flex flex-wrap gap-2.5">
                {INDUSTRIES.map((item, i) => (
                  <motion.span
                    key={item}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: false, margin: "-60px" }}
                    transition={{ duration: 0.3, delay: i * 0.03 }}
                    className="rounded-full border border-navy-900/10 bg-navy-50 px-4 py-2 text-sm font-medium text-navy-800"
                  >
                    {item}
                  </motion.span>
                ))}
              </div>
            </Reveal>

            <Reveal>
              <div className="rounded-3xl border border-slate-200 bg-[rgb(238,242,250)]/60 p-8 md:p-10">
                <SectionHeading icon={Target}>Our Commitment</SectionHeading>
                <p className="mt-3 text-[15px] text-slate-600">Every assignment entrusted to us is guided by four commitments.</p>
                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                  {VALUES.map((c, i) => (
                    <motion.div
                      key={c.title}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: false, margin: "-60px" }}
                      transition={{ duration: 0.45, delay: i * 0.08 }}
                      whileHover={{ y: -3 }}
                      className="flex flex-col items-start gap-3 rounded-2xl bg-white p-5 shadow-[0_4px_16px_rgba(0,0,0,0.03)]"
                    >
                      <span className="grid h-10 w-10 place-items-center rounded-xl bg-navy-900 text-gold-400">
                        <c.icon className="h-5 w-5" />
                      </span>
                      <h4 className="text-sm font-semibold text-navy-900">{c.title}</h4>
                      <p className="text-sm leading-relaxed text-slate-600">{c.desc}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal>
            <div className="mt-16 flex flex-col items-start gap-6 rounded-3xl border border-navy-900/10 bg-navy-900 p-8 md:flex-row md:items-center md:justify-between md:p-10">
              <div>
                <h3 className="text-xl font-semibold text-white md:text-2xl">Whether you're a first-time entrepreneur or an established enterprise —</h3>
                <p className="mt-2 text-sm text-white/70 md:text-base">we're committed to dependable, practical and value-driven business solutions.</p>
              </div>
              <Link
                to="/contact"
                data-testid="about-cta-contact"
                className="group relative inline-flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-full bg-[#f2a40b] px-5 py-3 text-xs sm:text-sm font-bold text-[#08254a] shadow-[0_8px_22px_rgba(242,164,11,0.35)] ring-1 ring-amber-400/40 transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_12px_28px_rgba(242,164,11,0.5)] active:scale-[0.98]"
              >
                <span className="grid h-7 w-7 sm:h-8 sm:w-8 shrink-0 place-items-center rounded-full bg-black/10 transition-transform duration-300 group-hover:rotate-12">
                  <Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#08254a]" />
                </span>
                <span className="font-bold tracking-tight">Talk to Our Team</span>
                <span className="grid h-7 w-7 sm:h-8 sm:w-8 shrink-0 place-items-center rounded-full bg-black/10 transition-transform duration-300 group-hover:translate-x-0.5">
                  <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#08254a]" />
                </span>
              </Link>
            </div>
          </Reveal>
        </div>
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
